import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Share2 } from 'lucide-react';
import { blogPosts } from '../data/mockData';

export const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="bg-[rgb(17,17,19)] min-h-screen pt-32">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              Article Not Found
            </h1>
            <Link
              to="/blog"
              className="inline-flex items-center space-x-2 text-[rgb(0,250,99)] hover:underline"
            >
              <ArrowLeft size={20} />
              <span>Back to Blog</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[rgb(17,17,19)] min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-12">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center space-x-2 text-[rgb(0,250,99)] hover:underline mb-8"
            >
              <ArrowLeft size={20} />
              <span>Back to Insights</span>
            </Link>

            <div className="mb-6">
              <span className="px-3 py-1 bg-[rgba(0,250,99,0.1)] border border-[rgba(0,250,99,0.3)] text-[rgb(0,250,99)] text-sm font-semibold rounded-full">
                {post.category}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {post.title}
            </h1>

            <div className="flex items-center space-x-6 text-[rgb(161,161,170)]">
              <div className="flex items-center space-x-2">
                <Calendar size={18} />
                <span>{new Date(post.date).toLocaleDateString('en-US', { 
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock size={18} />
                <span>{post.readTime}</span>
              </div>
              <button className="flex items-center space-x-2 hover:text-[rgb(0,250,99)] transition-colors">
                <Share2 size={18} />
                <span>Share</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="pb-12">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl overflow-hidden border border-[rgba(255,255,255,0.1)]">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="pb-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <article className="prose prose-invert prose-lg max-w-none">
              <div className="text-[rgb(218,218,218)] leading-relaxed space-y-6">
                <p className="text-xl text-[rgb(218,218,218)] font-medium">
                  {post.excerpt}
                </p>

                <div className="h-px bg-[rgba(255,255,255,0.1)] my-8" />

                <p>
                  This is where the full blog post content would appear. In a real implementation,
                  this content would be stored in the database and dynamically rendered based on
                  the blog post ID or slug.
                </p>

                <h2 className="text-3xl font-bold text-white mt-12 mb-4">
                  Key Takeaways
                </h2>

                <ul className="space-y-3 list-none">
                  {[
                    'Healthcare marketing requires specialized knowledge and compliance',
                    'Digital strategies must balance innovation with regulatory requirements',
                    'Patient-centric content builds trust and drives engagement',
                    'Data-driven approaches deliver measurable results'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-[rgb(0,250,99)] rounded-full mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <h2 className="text-3xl font-bold text-white mt-12 mb-4">
                  Conclusion
                </h2>

                <p>
                  Healthcare marketing continues to evolve, and staying ahead requires both
                  strategic thinking and practical execution. At Onvyn, we help healthcare
                  brands navigate these challenges with clarity, compliance, and creative
                  excellence.
                </p>
              </div>
            </article>

            {/* Author Info */}
            <div className="mt-16 p-8 bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 rounded-full bg-[rgba(0,250,99,0.1)] flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-[rgb(0,250,99)]">O</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {post.author}
                  </h3>
                  <p className="text-[rgb(161,161,170)] leading-relaxed">
                    Healthcare marketing experts dedicated to helping brands communicate
                    science clearly and grow responsibly.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 p-8 bg-[rgb(26,28,30)] border border-[rgb(0,250,99)] rounded-2xl text-center">
              <h3 className="text-2xl font-bold text-white mb-4">
                Ready to Grow Your Healthcare Brand?
              </h3>
              <p className="text-[rgb(218,218,218)] mb-6">
                Let's discuss how we can help you achieve your marketing goals
              </p>
              <Link
                to="/contact"
                className="inline-block px-8 py-4 bg-[rgb(0,250,99)] text-[rgb(17,17,19)] rounded-xl font-semibold hover:bg-[rgb(0,220,87)] transition-all duration-200"
              >
                Schedule a Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      <section className="py-16 bg-[rgb(26,28,30)]">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogPosts
                .filter((p) => p.id !== post.id)
                .slice(0, 3)
                .map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-300 hover:border-[rgb(0,250,99)]"
                  >
                    <div className="relative h-48 overflow-hidden bg-[rgb(38,40,42)]">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[rgb(0,250,99)] transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-[rgb(161,161,170)] text-sm line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
